import recaptcha from '@components/App/RecaptchaWidget/recaptcha'
import mitt, { type Emitter } from 'mitt'

import { isAbortSignalError } from '@/utils/requests'

const FETCH_TIMEOUT_S = 15
const DATA_PREFIX = 'data: '

type PostEventStreamOptions = Partial<{
  timeoutSec: number
  attempts: number
}>

type RawStreamEvents = {
  attempt: number
  message: string
  success: undefined
  abort: undefined
  error: unknown
  attemptsWasted: undefined
}

const ABORT_TIMEOUT_REASON = 'Timeout'

export function postEventStreamV2(
  url: string,
  body: object,
  options?: PostEventStreamOptions
): [Emitter<RawStreamEvents>, () => void] {
  const { timeoutSec = FETCH_TIMEOUT_S, attempts = 5 } = options || {}

  const emitter = mitt<RawStreamEvents>()
  let abortController: AbortController
  const abort = () => abortController.abort()

  setTimeout(async () => {
    for (let attemptNumber = 0; attemptNumber < attempts; attemptNumber++) {
      abortController = new AbortController()
      try {
        const timeoutId = setTimeout(() => {
          abortController.abort(ABORT_TIMEOUT_REASON)
        }, timeoutSec * 1000)

        emitter.emit('attempt', attemptNumber)
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...body,
            response_key: await recaptcha.execute({
              action: 'postEventStreamV2',
            }),
          }),
          signal: abortController.signal,
        }).finally(() => {
          clearTimeout(timeoutId)
        })

        const reader = response.body
          ?.pipeThrough(new TextDecoderStream())
          .getReader()

        while (true) {
          const result = await reader?.read()
          if (result?.done || !result?.value) {
            emitter.emit('success')
            return
          }
          const messages = result.value.split('\n\n').filter(Boolean)
          for (const message of messages) {
            if (!message.startsWith(DATA_PREFIX)) {
              throw new Error(`Invalid format: ${message}`)
            }
            const parsedMessage = message
              .slice(DATA_PREFIX.length)
              .replaceAll('\\n', '\n')
            emitter.emit('message', parsedMessage)
          }
        }
      } catch (e) {
        if (isAbortSignalError(e)) {
          emitter.emit('abort')
          return
        }
        if (
          abortController.signal.aborted &&
          abortController.signal.reason === ABORT_TIMEOUT_REASON
        ) {
          emitter.emit('error', ABORT_TIMEOUT_REASON)
          continue
        }
        abortController.abort()
        emitter.emit('error', e)
      }
    }
    emitter.emit('attemptsWasted')
  }, 0)

  return [emitter, abort]
}

type ParsedScvStreamEvents = {
  cell: {
    value: string
    rowNumber: number
    colNumber: number
    terminal: boolean
  }
  row: {
    rowNumber: number
    value: string[]
  }
}

type Options = {
  delimiter?: string
}

export const parseCsv = (
  streamEmitter: Emitter<RawStreamEvents>,
  opts?: Options
): Emitter<ParsedScvStreamEvents> => {
  const { delimiter = ';' } = opts || {}

  const emitter = mitt<ParsedScvStreamEvents>()

  let buffer = ''
  let rowNumber = 0
  let colNumber = 0
  // @ts-expect-error
  let rowValues = []

  streamEmitter.on('attempt', () => {
    buffer = ''
    rowNumber = 0
    colNumber = 0
    rowValues = []
  })

  streamEmitter.on('message', (message) => {
    buffer += message

    while (true) {
      const indexOfDelimiter = buffer.indexOf(delimiter)
      const indexOfNewLine = buffer.indexOf('\n')
      const minIndexOfDelimiter =
        indexOfDelimiter === -1
          ? indexOfNewLine
          : indexOfNewLine === -1
            ? indexOfDelimiter
            : Math.max(indexOfDelimiter, indexOfNewLine)

      if (minIndexOfDelimiter === -1) {
        buffer &&
          emitter.emit('cell', {
            value: buffer,
            rowNumber,
            colNumber,
            terminal: false,
          })
        break
      }

      const cellValue = buffer.slice(0, minIndexOfDelimiter).trim()
      rowValues.push(cellValue)
      emitter.emit('cell', {
        value: cellValue,
        rowNumber,
        colNumber,
        terminal: true,
      })
      if (minIndexOfDelimiter === indexOfNewLine) {
        emitter.emit('row', {
          rowNumber,
          // @ts-expect-error
          value: rowValues,
        })
        rowNumber++
        colNumber = 0
        rowValues = []
      } else {
        colNumber++
      }
      buffer = buffer.slice(minIndexOfDelimiter + 1)
    }
  })

  streamEmitter.on('success', () => {
    rowValues.push(buffer.trim())
    emitter.emit('cell', {
      value: buffer.trim(),
      rowNumber,
      colNumber,
      terminal: true,
    })
    emitter.emit('row', {
      rowNumber,
      // @ts-expect-error
      value: rowValues,
    })
  })

  return emitter
}

type ParsedLineStreamEvents = {
  block: {
    value: string
    index: number
    terminal?: boolean
  }
}

export const parseBlocks = (
  streamEmitter: Emitter<RawStreamEvents>
): Emitter<ParsedLineStreamEvents> => {
  const emitter = mitt<ParsedLineStreamEvents>()

  let buffer = ''
  let index = 0

  streamEmitter.on('message', (message) => {
    buffer += message
    while (buffer.includes(';')) {
      emitter.emit('block', {
        value: buffer.slice(0, buffer.indexOf(';')),
        index,
        terminal: true,
      })
      buffer = buffer.slice(buffer.indexOf(';') + 1)
      index++
    }
    if (buffer.length) {
      emitter.emit('block', {
        value: buffer,
        index,
      })
    }
  })

  streamEmitter.on('success', () => {
    emitter.emit('block', {
      value: buffer,
      index,
      terminal: true,
    })
  })

  return emitter
}
